import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Activity, Calendar, Clock, CheckCircle2, ChevronUp, ChevronDown } from "lucide-react";
import { ddmmyyyy, time12h, timeRange12h } from "../lib/dates";
import { localYMD } from "../lib/dates";

/**
 * Auto-refreshing live bookings card.
 * Categorises bookings into:
 *   • Ongoing            — start ≤ now < end (today only)
 *   • Upcoming Today     — now < start (today)
 *   • Upcoming (future)  — booking_date > today (advance bookings, kept until their slot end-time passes)
 *   • Completed Today    — end ≤ now (today)
 * Polls every 30s.
 */
function ymd(d) { return localYMD(d); }

function categorise(items) {
    const now = new Date();
    const today = ymd(now);
    const out = { ongoing: [], upcomingToday: [], upcomingFuture: [], completedToday: [] };
    for (const b of items) {
        try {
            const start = new Date(`${b.booking_date}T${b.start_time}:00`);
            const end = new Date(`${b.booking_date}T${b.end_time}:00`);
            if (b.booking_date === today) {
                if (now >= start && now < end) out.ongoing.push(b);
                else if (now < start) out.upcomingToday.push(b);
                else out.completedToday.push(b);
            } else if (b.booking_date > today) {
                // Future date — kept here until end-time has passed
                if (now < end) out.upcomingFuture.push(b);
            }
        } catch (_) { /* skip */ }
    }
    out.ongoing.sort((a, b) => a.start_time.localeCompare(b.start_time));
    out.upcomingToday.sort((a, b) => a.start_time.localeCompare(b.start_time));
    out.upcomingFuture.sort((a, b) => (a.booking_date + a.start_time).localeCompare(b.booking_date + b.start_time));
    out.completedToday.sort((a, b) => b.start_time.localeCompare(a.start_time));
    return out;
}

const SECTIONS = [
    { key: "ongoing", label: "Ongoing", icon: Activity, accent: "text-[#00FF66]", dot: "bg-[#00FF66]" },
    { key: "upcomingToday", label: "Upcoming Today", icon: Clock, accent: "text-sky-400", dot: "bg-sky-400" },
    { key: "upcomingFuture", label: "Upcoming (Advance)", icon: Calendar, accent: "text-amber-400", dot: "bg-amber-400" },
    { key: "completedToday", label: "Completed Today", icon: CheckCircle2, accent: "text-slate-400", dot: "bg-slate-500" },
];

export default function LiveBookingsCard({ endpoint = "/admin/live-bookings" }) {
    const [items, setItems] = useState([]);
    const [pulse, setPulse] = useState(false);
    const [detail, setDetail] = useState(null);
    const [openSec, setOpenSec] = useState({ ongoing: true, upcomingToday: true, upcomingFuture: true, completedToday: false });
    const [seeAll, setSeeAll] = useState({ ongoing: false, upcomingToday: false, upcomingFuture: false, completedToday: false });

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                // Ask for a wider list — backend filters to end_time>now (today + future)
                const { data } = await api.get(endpoint, { params: { limit: 80 } });
                if (alive) {
                    setItems(prev => {
                        const newIds = new Set(data.map(b => b.id));
                        const oldIds = new Set(prev.map(b => b.id));
                        if ([...newIds].some(id => !oldIds.has(id))) setPulse(true);
                        return data;
                    });
                    setTimeout(() => setPulse(false), 800);
                }
            } catch (e) { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 30000);
        // Re-render every minute so sections move from Upcoming → Ongoing → Completed at the right time
        const ticker = setInterval(() => setItems(p => [...p]), 60000);
        return () => { alive = false; clearInterval(id); clearInterval(ticker); };
    }, [endpoint]);

    const buckets = useMemo(() => categorise(items), [items]);
    const total = buckets.ongoing.length + buckets.upcomingToday.length + buckets.upcomingFuture.length + buckets.completedToday.length;

    return (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5" data-testid="live-bookings-card">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Activity className={`w-4 h-4 ${pulse ? "text-[#00FF66] animate-pulse" : "text-slate-400"}`} />
                    <h3 className="font-display text-lg font-bold uppercase text-white">Live Bookings</h3>
                    <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#00FF66] bg-[#00FF66]/15 border border-[#00FF66]/40 px-2 py-0.5 rounded-full">
                        <span className={`w-1.5 h-1.5 rounded-full bg-[#00FF66] ${pulse ? "animate-pulse" : ""}`} /> Live
                    </span>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-slate-500" data-testid="live-total-count">{total}</span>
            </div>

            {total === 0 ? (
                <div className="text-center text-slate-500 py-6 text-sm">No bookings.</div>
            ) : (
                <div className="space-y-3 max-h-[520px] overflow-y-auto">
                    {SECTIONS.map(sec => {
                        const list = buckets[sec.key];
                        if (list.length === 0) return null;
                        const open = openSec[sec.key];
                        const Icon = sec.icon;
                        return (
                            <div key={sec.key} className="bg-slate-950/40 border border-slate-800 rounded" data-testid={`live-section-${sec.key}`}>
                                <button type="button"
                                    onClick={() => setOpenSec(prev => ({ ...prev, [sec.key]: !prev[sec.key] }))}
                                    className="w-full flex items-center justify-between px-3 py-2 text-left">
                                    <div className="flex items-center gap-2">
                                        <span className={`inline-flex w-2 h-2 rounded-full ${sec.dot} ${sec.key === "ongoing" ? "animate-pulse" : ""}`} />
                                        <Icon className={`w-3.5 h-3.5 ${sec.accent}`} />
                                        <span className={`text-[11px] font-bold uppercase tracking-widest ${sec.accent}`}>{sec.label}</span>
                                        <span className="text-[10px] text-slate-500 ml-1">{list.length}</span>
                                    </div>
                                    {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                                </button>
                                {open && (
                                    <div className="px-2 pb-2 space-y-1.5">
                                        {(seeAll[sec.key] ? list : list.slice(0, 5)).map(b => (
                                            <button key={b.id} type="button" onClick={() => setDetail(b)} data-testid={`live-row-${b.id}`}
                                                className="w-full text-left flex items-center justify-between bg-slate-900 border border-slate-800 rounded p-2.5 hover:border-[#00FF66]/50 transition">
                                                <div className="flex-1 min-w-0">
                                                    {/* Iter 32 / A7 — date is the most-important info at a glance */}
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-display font-bold text-white text-sm">
                                                            {ddmmyyyy(b.booking_date)} · {time12h(b.start_time)}–{time12h(b.end_time)}
                                                        </span>
                                                        <span className="text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">
                                                            {b.source === "offline" ? "Offline" : (b.source === "external" ? "Other App" : "Online")}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-slate-400 truncate">{b.user_name} · {b.centre_name}</div>
                                                    <div className="text-[10px] text-slate-500 mt-0.5">
                                                        <span className="opacity-70">ID</span> <span>{b.booking_code}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-bold text-[#00FF66]">₹{b.paid_amount}</div>
                                                    {b.balance_due > 0 && <div className="text-[10px] text-amber-400">+₹{b.balance_due}</div>}
                                                </div>
                                            </button>
                                        ))}
                                        {list.length > 5 && (
                                            <button type="button"
                                                onClick={() => setSeeAll(prev => ({ ...prev, [sec.key]: !prev[sec.key] }))}
                                                data-testid={`see-all-${sec.key}`}
                                                className="w-full text-center text-[10px] font-bold uppercase tracking-widest text-[#00FF66] hover:text-[#00cc52] py-2 border border-dashed border-[#00FF66]/30 rounded hover:bg-[#00FF66]/5">
                                                {seeAll[sec.key] ? `Show less` : `See all ${list.length} →`}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {detail && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="live-detail-modal">
                    <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-2">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-display text-xl font-bold uppercase text-white">{detail.booking_code}</h3>
                            <button onClick={() => setDetail(null)} className="text-slate-500 hover:text-white" data-testid="live-detail-close">✕</button>
                        </div>
                        <Row label="Source" value={detail.source === "offline" ? "Offline" : "Online"} />
                        <Row label="Name" value={detail.user_name} />
                        <Row label="Phone" value={detail.user_phone || "—"} />
                        <Row label="Centre" value={detail.centre_name} />
                        <Row label="Facility / Sport" value={detail.facility_name || detail.turf_name || detail.sport} />
                        <Row label="Date" value={ddmmyyyy(detail.booking_date)} />
                        <Row label="Timing" value={`${time12h(detail.start_time)} – ${time12h(detail.end_time)}`} />
                        <Row label="Payment method" value={detail.payment_method || detail.payment_mode || "online"} />
                        <Row label="Payment status" value={detail.payment_status || (detail.balance_due > 0 ? "partial" : "paid")} />
                        <Row label="Amount" value={`₹${detail.total || detail.paid_amount}`} bold />
                        <Row label="Paid" value={`₹${detail.paid_amount}`} />
                        {detail.balance_due > 0 && <Row label="Balance" value={`₹${detail.balance_due}`} amber />}
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, bold, amber }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">{label}</span>
            <span className={`${bold ? "font-display font-bold text-lg text-white" : amber ? "text-amber-400" : "text-white"}`}>{value}</span>
        </div>
    );
}
